import React, { useState } from 'react'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'
import { auth, db, functions, ENV_SUFFIX } from '../../firebase'
import { useForm, useError } from '../../hooks'
import TextField from '../Form/TextField'
import Button from '../Button'
import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { toast } from 'react-hot-toast'
import { Eye, EyeOff } from 'lucide-react'

export default function Login() {
  const setError = useError()
  const navigate = useNavigate()
  const [getFieldProps, formData] = useForm()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    const email = formData.email?.toLowerCase().trim()
    const { password } = formData

    setLoading(true)
    try {
      const getUserByEmailFn = httpsCallable(
        functions,
        `getUserByEmail${ENV_SUFFIX}`
      )
      const response = await getUserByEmailFn({ email })

      const userData = response.data

      if (!userData) {
        toast.error('No user found with this email.')
        return
      }

      if (userData.isApproved === false) {
        toast.error(
          'Your account is pending approval from the admin. Please wait until it is approved to log in'
        )
        return
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      )
      const user = userCredential.user

      const appSettingsRef = doc(db, 'appSettings', 'settings')
      const appSettingsSnap = await getDoc(appSettingsRef)

      const isMFARequired = appSettingsSnap.exists()
        ? appSettingsSnap.data().isMFARequired
        : false

      if (userData?.isTwoFactorEnabled) {
        localStorage.setItem('mfaInProgress', 'true')
        localStorage.setItem('mfaUserId', user.uid)
        navigate('/verify-otp', { state: { userData, userId: user.uid } })
      } else if (isMFARequired) {
        localStorage.setItem('mfaInProgress', 'true')
        localStorage.setItem('mfaUserId', user.uid)
        navigate('/mfa-setup', { state: { userData, userId: user.uid } })
      } else {
        localStorage.removeItem('mfaInProgress')
        localStorage.removeItem('mfaUserId')
        navigate('/dispatch')
      }

      toast.success('Login successful!')
    } catch (err) {
      console.error('Login error:', err)

      // An administrator account now comes back as not-found to an
      // anonymous caller, exactly like an address with no account at all, so
      // both land on the same message they already showed.
      if (err.code === 'functions/not-found') {
        toast.error('No account found with this email address.', {
          id: 'login-failed'
        })
      } else if (
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/wrong-password'
      ) {
        toast.error(
          'The email or password you entered is incorrect. Please try again.',
          { id: 'login-failed' }
        )
      } else {
        setError(err)
        toast.error('Something went wrong. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='login-container'>
      <div className='login-grid'>
        <div className='login-left login-centered'>
          <div className='login-form'>
            <div className='login-logo'>
              <img
                className='login'
                src='/assets/images/live-alarm.png'
                alt='login visual'
              />
            </div>
            <form onSubmit={handleSubmit}>
              <div className='login-input'>
                <TextField
                  label='Email'
                  name='email'
                  type='email'
                  required={true}
                  autoComplete='off'
                  placeholder='Email'
                  {...getFieldProps()}
                />
                <div style={{ position: 'relative' }}>
                  <TextField
                    label='Password'
                    name='password'
                    type={showPassword ? 'text' : 'password'}
                    required={true}
                    autoComplete='off'
                    placeholder='Password'
                    {...getFieldProps()}
                  />
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '34px',
                      cursor: 'pointer',
                      color: '#555'
                    }}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </span>
                </div>
              </div>
              <div className='login-options'>
                <a href='/forgot-password' className='login-forgot'>
                  Forgot Password?
                </a>
              </div>

              <Button type='submit' className='login-button' disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </Button>

              <div className='login-signup'>
                <label className='login-label'>Don't have an account?</label>
                <a
                  href='/signup'
                  className='login-forgot'
                  style={{ textDecoration: 'none' }}>
                  <strong>Sign Up</strong>
                </a>
              </div>
            </form>
          </div>
        </div>
        <div className='login-right login-centered login-overflow'>
          <img
            className='login-image'
            src='/assets/images/logo.png'
            alt='login visual'
          />
        </div>
      </div>
    </div>
  )
}
